/**
 * Cabinet presentation mode.
 *
 * The cabinet shell (marquee + bezel + control panel) is the brand. But
 * once you're locked into a cartridge, you want the screen as big as
 * possible — real arcades work the same way.
 *
 * Two modes:
 * - `attract` — marquee + control panel visible. Used at boot, the
 *   attract loop (when it lands), the game selector grid, GAME OVER,
 *   settings.
 * - `playing` — marquee + control panel collapsed to 0 height; the
 *   screen + canvas grow to fill the freed space. Activated when a
 *   cartridge starts; deactivated on game-over / exit.
 *
 * The CSS does all the visual work via `body[data-cabinet-mode='...']`
 * selectors in `shell.css` — this module just flips the attribute.
 */

import { recomputeBezelFit } from './bezel-fit.js';

export type CabinetMode = 'attract' | 'playing';

const MODES: readonly CabinetMode[] = ['attract', 'playing'] as const;

/**
 * Set the cabinet's presentation mode. Idempotent — calling with the
 * same value twice is a no-op (we skip the DOM write).
 */
export function setCabinetMode(mode: CabinetMode): void {
  if (!MODES.includes(mode)) {
    throw new Error(`Unknown cabinet mode: ${mode as string}`);
  }
  if (typeof document === 'undefined') {
    // SSR / tests without DOM — nothing to do.
    return;
  }
  if (document.body.dataset.cabinetMode === mode) {
    return;
  }
  document.body.dataset.cabinetMode = mode;
  // Mode flip collapses or expands marquee + panel rows, which changes
  // the screen row's height. Recompute the bezel fit on the next frame
  // (after the grid-template-rows transition starts). The transition
  // itself is 280ms; the ResizeObserver picks up subsequent frames
  // automatically, but the leading edge benefits from an explicit nudge.
  requestAnimationFrame(() => recomputeBezelFit());
}

/**
 * Read the current cabinet mode. Defaults to `attract` when nothing has
 * been set yet (matches CSS default).
 */
export function getCabinetMode(): CabinetMode {
  if (typeof document === 'undefined') {
    return 'attract';
  }
  const raw = document.body.dataset.cabinetMode;
  if (raw === 'playing' || raw === 'attract') {
    return raw;
  }
  return 'attract';
}
